import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import {
  changeUpdateBiomarkerModalVisibility,
  setBiomarkerToEdit,
  setBiomarkersList,
  setBiomarkersCount,
  changeDeleteBiomarkerModalVisibility,
  setFoodList,
  setFoodOptions
} from '../../../../store/appSlice';
import { RootState } from '../../../../store/store';
import Button from '../../../BaseComponents/Button';
import Modal from '../../../BaseComponents/Modal';
import MultiSelect from '../../../BaseComponents/MultiSelect';
import Select from '../../../BaseComponents/Select';
import TextInput from '../../../BaseComponents/TextInput';
import GenderValuesBlock from './GenderValuesBlock';
import './UpdateBiomarkerModal.scss';
import TextArea from '../../../BaseComponents/TextArea';
import {
  BiomarkerType,
  BiomarkerLowHighGood,
  CreateBiomarkerRequest,
  DerivedBiomarkerFormulaType,
  DerivedBiomarkerScaleTarget,
  GenderRangeData,
  UpdateBiomarkerRequest,
  ListBiomarkerResponseElement
} from '../../../../types/apiGeneratedTypes';
import {
  createBiomarker,
  updateBiomarker,
  getBiomarkersList,
  deleteBiomarker
} from '../../../../api/biomarkers';
import { ReactComponent as BinIcon } from '../../../../assets/icons/TrashBin2.svg';
import ConfirmModal from '../../../BaseComponents/Modal/ConfirmModal';
import { biomarkerMeasurementUnits } from '../../../../utils/constants';
import { getFoodList } from '../../../../api/food';
import MultiSelectSearch from '../../../BaseComponents/MultiSelectSearch';

export interface FormulaTermFormValue {
  biomarker: { key: any; value: any };
  coefficient: string;
}

export interface FormValues {
  name: string;
  description: string;
  lab_bm_id: string;
  catalog_id: string;
  price: string;
  bodySystems: { key: any; value: any }[];
  units: { key: any; value: any };
  foods_to_eat: { key: any; value: any }[];
  foods_to_avoid: { key: any; value: any }[];
  lifestyle_changes: string;
  follow_up: string;
  biomarker_type: { key: BiomarkerType; value: string };
  contraindications: string;
  value_meaning: 'POSITIVE_NORMAL' | 'POSITIVE_ABNORMAL';
  age_dependent: boolean;
  is_derived: boolean;
  blood_panels: { key: string; value: string }[];
  formula_type: { key: DerivedBiomarkerFormulaType; value: string };
  numerator: { key: any; value: any };
  denominator: { key: any; value: any };
  scale_target: { key: DerivedBiomarkerScaleTarget | ''; value: string };
  scale_factor: string;
  formula_constant: string;
  formula_terms: FormulaTermFormValue[];
  low_high_good: { key: BiomarkerLowHighGood; value: string };
  retest_time_period: { key: string; value: string };
  genderData: {
    gender: { key: any; value: any };
    data_with_age: {
      age_from: string;
      age_to: string;
      low_from: string;
      low_to: string;
      below_optimal_from: string;
      below_optimal_to: string;
      above_optimal_from: string;
      above_optimal_to: string;
      optimal_from: string;
      optimal_to: string;
      high_from: string;
      high_to: string;
      high_to_infinity: boolean;
      low_to_infinity?: boolean;
      below_optimal_to_infinity?: boolean;
      optimal_to_infinity?: boolean;
      above_optimal_to_infinity?: boolean;
    }[];
    low_from: string;
    low_to: string;
    high_from: string;
    high_to: string;
    high_to_infinity: boolean;
    below_optimal_from: string;
    below_optimal_to: string;
    above_optimal_from: string;
    above_optimal_to?: string;
    optimal_from: string;
    optimal_to: string;
    low_to_infinity?: boolean;
    below_optimal_to_infinity?: boolean;
    optimal_to_infinity?: boolean;
    above_optimal_to_infinity?: boolean;
  }[];
}
const biomarkerTypesList: { key: BiomarkerType; value: string }[] = [
  { key: BiomarkerType.RANGES, value: 'Ranges' },
  { key: BiomarkerType.BINARY, value: 'Positive / Negative value' },
  { key: BiomarkerType.UNITS, value: 'Units + Values' }
];

const bloodPanelOptions: { key: string; value: string }[] = [
  { key: 'core_panel', value: 'Core Panel' },
  { key: 'repeat_panel', value: 'Repeat Panel' },
  { key: 'plan_type', value: 'Plan Type' }
];

const formulaTypeOptions: {
  key: DerivedBiomarkerFormulaType;
  value: string;
}[] = [
  { key: DerivedBiomarkerFormulaType.RATIO, value: 'Ratio (A / B)' },
  {
    key: DerivedBiomarkerFormulaType.AFFINE,
    value: 'Affine (Σ coefficient × biomarker + constant)'
  }
];

const scaleTargetOptions: {
  key: DerivedBiomarkerScaleTarget | '';
  value: string;
}[] = [
  { key: '', value: 'None' },
  { key: DerivedBiomarkerScaleTarget.NUMERATOR, value: 'Numerator' },
  { key: DerivedBiomarkerScaleTarget.DENOMINATOR, value: 'Denominator' },
  { key: DerivedBiomarkerScaleTarget.VALUE, value: 'Result (value)' }
];

const emptyFormulaTerm = (): FormulaTermFormValue => ({
  biomarker: { key: '', value: '' },
  coefficient: ''
});

const lowHighGoodOptions: { key: BiomarkerLowHighGood; value: string }[] = [
  { key: BiomarkerLowHighGood.NORMAL, value: 'Normal' },
  { key: BiomarkerLowHighGood.LOW_IS_GOOD, value: 'Low is good' },
  { key: BiomarkerLowHighGood.HIGH_IS_GOOD, value: 'High is good' }
];

const retestTimePeriodOptions: { key: string; value: string }[] = Array.from(
  { length: 365 },
  (_, index) => ({ key: `${index + 1}`, value: `${index + 1}` })
);

const findBiomarkerName = (
  biomarkerId: number,
  sources: ListBiomarkerResponseElement[][]
): string => {
  for (const source of sources) {
    const match = source.find((b) => Number(b.id) === Number(biomarkerId));
    if (match?.name) return match.name;
  }
  return '';
};

/** Syncs affine term dropdown labels once the full biomarker list loads. */
const AffineFormulaTermsLabelSync: React.FC<{
  allBiomarkers: ListBiomarkerResponseElement[];
  formulaTerms: FormulaTermFormValue[];
  formulaTypeKey: DerivedBiomarkerFormulaType;
  setFieldValue: (field: string, value: FormulaTermFormValue[]) => void;
}> = ({ allBiomarkers, formulaTerms, formulaTypeKey, setFieldValue }) => {
  useEffect(() => {
    if (
      !allBiomarkers.length ||
      formulaTypeKey !== DerivedBiomarkerFormulaType.AFFINE
    ) {
      return;
    }

    let changed = false;
    const nextTerms = formulaTerms.map((term) => {
      if (!term.biomarker?.key) return term;
      const name = findBiomarkerName(Number(term.biomarker.key), [allBiomarkers]);
      if (name && term.biomarker.value !== name) {
        changed = true;
        return {
          ...term,
          biomarker: { key: term.biomarker.key, value: name }
        };
      }
      return term;
    });

    if (changed) {
      setFieldValue('formula_terms', nextTerms);
    }
    // Only re-resolve when the full biomarker catalog arrives; term edits are handled by the Select onChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBiomarkers, formulaTypeKey, setFieldValue]);

  return null;
};

export const UpdateBiomarkerModal: React.FC = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    isUpdateBiomarkerModalOpened,
    biomarkerToEdit,
    bodySystemsList,
    isDeleteBiomarkerModalOpened,
    biomarkersList,
    foodListOptions
  } = useSelector((state: RootState) => state.app);

  // Seed from Redux list so affine term labels resolve on first Formik render
  // (modal mounts only when opened; async fetch alone is too late for initialValues).
  const [allBiomarkers, setAllBiomarkers] =
    useState<ListBiomarkerResponseElement[]>(biomarkersList);

  useEffect(() => {
    try {
      const fetchFoodList = async () => {
        const foodList = await getFoodList();
        dispatch(setFoodList(foodList.foods));
        dispatch(setFoodOptions(foodList.foods));
      };
      fetchFoodList();

      const fetchAllBiomarkers = async () => {
        try {
          const response = await getBiomarkersList();
          setAllBiomarkers(response.biomarkers);
        } catch (err) {
          console.error('Error fetching biomarkers list:', err);
        }
      };
      fetchAllBiomarkers();
    } catch (error) {
      console.error('Error fetching food list:', error);
    }
  }, [dispatch]);

  const bodySystemsOptions = useMemo(() => {
    return bodySystemsList.map((bodySystem) => ({
      key: bodySystem.id,
      value: bodySystem.title
    }));
  }, [bodySystemsList]);

  const biomarkerOptions = useMemo(() => {
    const source = allBiomarkers.length > 0 ? allBiomarkers : biomarkersList;
    return source.map((biomarker) => ({
      key: biomarker.id,
      value: biomarker.name
    }));
  }, [allBiomarkers, biomarkersList]);

  const handleClose = useCallback(() => {
    dispatch(changeUpdateBiomarkerModalVisibility(false));
    dispatch(setBiomarkerToEdit(null));
  }, [dispatch]);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        setIsLoading(true);
        setError(null);

        // Helper function to convert values properly handling infinity
        const convertValue = (value: string | undefined) => {
          if (value === 'NA' || value === undefined || value === '') return null;
          if (value === '∞') return null;
          return +value;
        };

        // Process gender data based on the selection(s) provided.
        let male_range_data: CreateBiomarkerRequest['male_range_data'] = null;
        let female_range_data: CreateBiomarkerRequest['female_range_data'] =
          null;
        const genderSelections = values.genderData;

        if (genderSelections && genderSelections.length > 0) {
          // Check if the first element has "all" selected by comparing the key.
          if (genderSelections[0].gender?.key === 'all') {
            const rangeData = {
              low_from: convertValue(genderSelections[0].low_from),
              low_to: convertValue(genderSelections[0].low_to),
              low_to_infinity: genderSelections[0].low_to === '∞',
              below_optimal_from: convertValue(genderSelections[0].below_optimal_from),
              below_optimal_to: convertValue(genderSelections[0].below_optimal_to),
              below_to_infinity: genderSelections[0].below_optimal_to === '∞',
              optimal_from: convertValue(genderSelections[0].optimal_from),
              optimal_to: convertValue(genderSelections[0].optimal_to),
              optimal_to_infinity: genderSelections[0].optimal_to === '∞',
              above_optimal_from: convertValue(genderSelections[0].above_optimal_from),
              above_optimal_to: convertValue(genderSelections[0].above_optimal_to),
              above_to_infinity: genderSelections[0].above_optimal_to === '∞',
              high_from: convertValue(genderSelections[0].high_from),
              high_to: convertValue(genderSelections[0].high_to),
              high_to_infinity: genderSelections[0].high_to === '∞'
            } as any;
            // Both genders receive the same range data.
            male_range_data = rangeData;
            female_range_data = rangeData;
          } else if (genderSelections.length === 1) {
            // When only one block is provided
            const rangeData = {
              low_from: convertValue(genderSelections[0].low_from),
              low_to: convertValue(genderSelections[0].low_to),
              low_to_infinity: genderSelections[0].low_to === '∞',
              below_optimal_from: convertValue(genderSelections[0].below_optimal_from),
              below_optimal_to: convertValue(genderSelections[0].below_optimal_to),
              below_to_infinity: genderSelections[0].below_optimal_to === '∞',
              optimal_from: convertValue(genderSelections[0].optimal_from),
              optimal_to: convertValue(genderSelections[0].optimal_to),
              optimal_to_infinity: genderSelections[0].optimal_to === '∞',
              above_optimal_from: convertValue(genderSelections[0].above_optimal_from),
              above_optimal_to: convertValue(genderSelections[0].above_optimal_to),
              above_to_infinity: genderSelections[0].above_optimal_to === '∞',
              high_from: convertValue(genderSelections[0].high_from),
              high_to: convertValue(genderSelections[0].high_to),
              high_to_infinity: genderSelections[0].high_to === '∞'
            } as any;

            if (genderSelections[0].gender?.key === 'male') {
              male_range_data = rangeData;
              female_range_data = null;
            } else if (genderSelections[0].gender?.key === 'female') {
              female_range_data = rangeData;
              male_range_data = null;
            }
          } else {
            // When multiple blocks exist—loop through and assign based on the gender key.
            genderSelections.forEach((data) => {
              const rangeData = {
                low_from: convertValue(data.low_from),
                low_to: convertValue(data.low_to),
                low_to_infinity: data.low_to_infinity ?? (data.low_to === '∞' ? true : false),
                below_optimal_from: convertValue(data.below_optimal_from),
                below_optimal_to: convertValue(data.below_optimal_to),
                below_to_infinity: data.below_optimal_to_infinity ?? (data.below_optimal_to === '∞' ? true : false),
                optimal_from: convertValue(data.optimal_from),
                optimal_to: convertValue(data.optimal_to),
                optimal_to_infinity: data.optimal_to_infinity ?? (data.optimal_to === '∞' ? true : false),
                above_optimal_from: convertValue(data.above_optimal_from),
                above_optimal_to: convertValue(data.above_optimal_to),
                above_to_infinity: data.above_optimal_to_infinity ?? (data.above_optimal_to === '∞' ? true : false),
                high_from: convertValue(data.high_from),
                high_to: convertValue(data.high_to),
                high_to_infinity: data.high_to_infinity ?? (data.high_to === '∞' ? true : false)
              } as any;

              if (data.gender?.key === 'male') {
                male_range_data = rangeData;
              } else if (data.gender?.key === 'female') {
                female_range_data = rangeData;
              }
            });
          }
        }

        const biomarker = {
          body_system_ids: values.bodySystems.map(
            (bodySystem) => bodySystem.key
          ),
          foods_to_avoid_ids: values.foods_to_avoid.length
            ? values.foods_to_avoid.map((food) => food.key)
            : [],
          foods_to_eat_ids: values.foods_to_eat.length
            ? values.foods_to_eat.map((food) => food.key)
            : [],
          blood_panels: values.blood_panels.length
            ? values.blood_panels.map((panel) => panel.key)
            : [],

          name: values.name,
          description: values.description,
          lab_bm_id: values.lab_bm_id,
          catalog_id: values.catalog_id || null,
          price: values.price !== '' ? Number(values.price) : null,
          lifestyle_changes: values.lifestyle_changes,
          retest_time_period: values.retest_time_period.key || null,
          follow_up: values.follow_up,
          contraindications: values.contraindications,
          biomarker_type: values.biomarker_type.key,
          is_derived: values.is_derived,
          formula_type: values.is_derived ? values.formula_type.key : null,
          numerator_biomarker_id:
            values.is_derived &&
            values.formula_type.key === DerivedBiomarkerFormulaType.RATIO
              ? values.numerator.key || null
              : null,
          denominator_biomarker_id:
            values.is_derived &&
            values.formula_type.key === DerivedBiomarkerFormulaType.RATIO
              ? values.denominator.key || null
              : null,
          scale_target:
            values.is_derived &&
            values.formula_type.key === DerivedBiomarkerFormulaType.RATIO &&
            values.scale_target.key
              ? values.scale_target.key
              : null,
          scale_factor:
            values.is_derived &&
            values.formula_type.key === DerivedBiomarkerFormulaType.RATIO &&
            values.scale_target.key &&
            values.scale_factor !== ''
              ? Number(values.scale_factor)
              : null,
          formula_constant:
            values.is_derived &&
            values.formula_type.key === DerivedBiomarkerFormulaType.AFFINE &&
            values.formula_constant !== ''
              ? Number(values.formula_constant)
              : null,
          formula_terms:
            values.is_derived &&
            values.formula_type.key === DerivedBiomarkerFormulaType.AFFINE
              ? values.formula_terms
                  .filter(
                    (term) => term.biomarker.key !== '' && term.coefficient !== ''
                  )
                  .map((term) => ({
                    biomarker_id: Number(term.biomarker.key),
                    coefficient: Number(term.coefficient)
                  }))
              : null,
          low_high_good: values.low_high_good.key,
          ...(() => {
            switch (values.biomarker_type.key) {
              case 'RANGES':
                if (!values.age_dependent) {
                  return {
                    age_dependent: values.age_dependent,
                    measurement_unit: values.units.value,
                    value_meaning: null,
                    female_range_data,
                    male_range_data
                  };
                }

                const genderData = values.genderData || [];

                const renameKeys = (obj: Record<string, any>) => {
                  // No renaming needed, just return the object as is
                  return obj;
                };

                const allItem = genderData.find((g) => g.gender?.key === 'all');
                if (allItem && allItem.data_with_age?.length) {
                  const allData = allItem.data_with_age.reduce(
                    (acc, item) => {
                      const key = `${item.age_from}-${item.age_to}`;
                      const { age_from, age_to, ...rest } = item;

                      const cleaned = Object.fromEntries(
                        Object.entries(rest).map(([k, v]) => {
                          if (k === 'high_to_infinity') return [k, v ?? false];
                          if (k === 'low_to_infinity') return [k, v ?? false];
                          if (k === 'below_optimal_to_infinity') return ['below_to_infinity', v ?? false];
                          if (k === 'optimal_to_infinity') return [k, v ?? false];
                          if (k === 'above_optimal_to_infinity') return ['above_to_infinity', v ?? false];
                          return [
                            k,
                            convertValue(v as string)
                          ];
                        })
                      );

                      // Ensure all infinity fields are always included
                      cleaned.high_to_infinity = rest.high_to_infinity ?? false;
                      cleaned.low_to_infinity = rest.low_to_infinity ?? false;
                      cleaned.below_to_infinity = rest.below_optimal_to_infinity ?? false;
                      cleaned.optimal_to_infinity = rest.optimal_to_infinity ?? false;
                      cleaned.above_to_infinity = rest.above_optimal_to_infinity ?? false;

                      acc[key] = renameKeys(cleaned);;
                      return acc;
                    },
                    {} as Record<string, any>
                  );

                  return {
                    age_dependent: true,
                    measurement_unit: values.units.value,
                    male_range_data: allData,
                    female_range_data: allData
                  };
                }

                const result: Record<string, any> = {
                  age_dependent: true,
                  measurement_unit: values.units.value
                };

                const genders = {
                  male: genderData.find((g) => g.gender?.key === 'male'),
                  female: genderData.find((g) => g.gender?.key === 'female')
                };

                for (const [genderKey, item] of Object.entries(genders)) {
                  if (item?.data_with_age?.length) {
                    const data = item.data_with_age.reduce(
                      (acc, entry) => {
                        const key = `${entry.age_from}-${entry.age_to}`;
                        const { age_from, age_to, ...rest } = entry;

                        const cleaned = Object.fromEntries(
                          Object.entries(rest).map(([k, v]) => {
                            if (k === 'high_to_infinity') return [k, v ?? false];
                            if (k === 'low_to_infinity') return [k, v ?? false];
                            if (k === 'below_optimal_to_infinity') return ['below_to_infinity', v ?? false];
                            if (k === 'optimal_to_infinity') return [k, v ?? false];
                            if (k === 'above_optimal_to_infinity') return ['above_to_infinity', v ?? false];
                            return [
                              k,
                              convertValue(v as string)
                            ];
                          })
                        ) as Record<string, any>;

                        // Ensure all infinity fields are always included
                        cleaned.high_to_infinity = rest.high_to_infinity ?? (rest.high_to === '∞' ? true : false);
                        cleaned.low_to_infinity = rest.low_to_infinity ?? (rest.low_to === '∞' ? true : false);
                        cleaned.below_to_infinity = rest.below_optimal_to_infinity ?? (rest.below_optimal_to === '∞' ? true : false);
                        cleaned.optimal_to_infinity = rest.optimal_to_infinity ?? (rest.optimal_to === '∞' ? true : false);
                        cleaned.above_to_infinity = rest.above_optimal_to_infinity ?? (rest.above_optimal_to === '∞' ? true : false);

                        acc[key] = cleaned;
                        return acc;
                      },
                      {} as Record<string, any>
                    );

                    result[`${genderKey}_range_data`] = data;
                  }
                }

                return result;

              case 'BINARY':
                return {
                  value_meaning: values.value_meaning,
                  age_dependent: null,
                  measurement_unit: null
                };
              case 'UNITS':
                return {
                  measurement_unit: values.units.value
                };
              default:
                return {};
            }
          })()
        } as CreateBiomarkerRequest;

        if (biomarkerToEdit) {
          await updateBiomarker({
            id: biomarkerToEdit.id,
            ...biomarker
          } as UpdateBiomarkerRequest);
        } else {
          await createBiomarker(biomarker);
        }

        const updatedBiomarkersList = await getBiomarkersList();
        dispatch(setBiomarkersList(updatedBiomarkersList.biomarkers));
        dispatch(setBiomarkersCount(updatedBiomarkersList.biomarkers.length));

        handleClose();
      } catch (err: any) {
        setError(err.message || 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    },
    [biomarkerToEdit, handleClose, dispatch]
  );

  const handleDeleteClick = useCallback(() => {
    dispatch(changeDeleteBiomarkerModalVisibility(true));
  }, [dispatch]);

  const handleCloseDeleteModalClick = useCallback(() => {
    dispatch(changeDeleteBiomarkerModalVisibility(false));
  }, [dispatch]);

  const handleDelete = useCallback(async () => {
    if (!biomarkerToEdit) return;
    try {
      setIsLoading(true);
      setError(null);
      await deleteBiomarker(biomarkerToEdit.id);
      dispatch(
        setBiomarkersList(
          biomarkersList.filter(
            (biomarker) => biomarker.id !== biomarkerToEdit.id
          )
        )
      );
      dispatch(setBiomarkerToEdit(null));
      dispatch(changeDeleteBiomarkerModalVisibility(false));
      handleClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [biomarkerToEdit, biomarkersList, dispatch, handleClose]);

  function isAgeDependentRange(data: any): boolean {
    if (typeof data !== 'object' || data === null) return false;

    const keys = Object.keys(data);

    return keys.every((key) => {
      const entry = data[key];
      if (!entry) return false;

      return typeof entry === 'object' && 'age_range' in entry;
    });
  }

  const isValidField = (v: any) => v != null && v !== '';

  return (
    <Modal
      isOpen={isUpdateBiomarkerModalOpened}
      onClose={handleClose}
      title={biomarkerToEdit ? 'Edit Biomarkers' : 'Add Biomarker'}
      width='800px'
    >
      <Formik<FormValues>
        initialValues={{
          name: biomarkerToEdit?.name || '',
          lab_bm_id: biomarkerToEdit?.lab_bm_id || '',
          catalog_id: (biomarkerToEdit as any)?.catalog_id || '',
          price: (biomarkerToEdit as any)?.price != null ? String((biomarkerToEdit as any)?.price) : '',
          description: biomarkerToEdit?.description || '',
          lifestyle_changes: biomarkerToEdit?.lifestyle_changes || '',
          retest_time_period: biomarkerToEdit?.retest_time_period
            ? retestTimePeriodOptions.find(
                (option) =>
                  option.key === String(biomarkerToEdit.retest_time_period)
              ) || { key: '', value: '' }
            : { key: '', value: '' },
          follow_up: biomarkerToEdit?.follow_up || '',
          contraindications: biomarkerToEdit?.contraindications || '',
          biomarker_type:
            biomarkerTypesList.find(
              (item) => item.key === biomarkerToEdit?.biomarker_type
            ) || biomarkerTypesList[0],
          value_meaning: biomarkerToEdit?.value_meaning || 'POSITIVE_ABNORMAL',
          age_dependent: biomarkerToEdit?.age_dependent || false,
          is_derived: biomarkerToEdit?.is_derived || false,
          formula_type:
            formulaTypeOptions.find(
              (option) =>
                option.key ===
                (biomarkerToEdit?.formula_type ||
                  (biomarkerToEdit?.formula_terms?.length
                    ? DerivedBiomarkerFormulaType.AFFINE
                    : DerivedBiomarkerFormulaType.RATIO))
            ) || formulaTypeOptions[0],
          numerator: biomarkerToEdit?.numerator_biomarker?.id
            ? {
              key: biomarkerToEdit.numerator_biomarker.id,
              value: biomarkerToEdit.numerator_biomarker.name || ''
            }
            : { key: '', value: '' },
          denominator: biomarkerToEdit?.denominator_biomarker?.id
            ? {
              key: biomarkerToEdit.denominator_biomarker.id,
              value: biomarkerToEdit.denominator_biomarker.name || ''
            }
            : { key: '', value: '' },
          scale_target: biomarkerToEdit?.scale_target
            ? scaleTargetOptions.find(
              (option) => option.key === biomarkerToEdit.scale_target
            ) || scaleTargetOptions[0]
            : scaleTargetOptions[0],
          scale_factor:
            biomarkerToEdit?.scale_factor != null
              ? String(biomarkerToEdit.scale_factor)
              : '',
          formula_constant:
            biomarkerToEdit?.formula_constant != null
              ? String(biomarkerToEdit.formula_constant)
              : '',
          formula_terms: biomarkerToEdit?.formula_terms?.length
            ? biomarkerToEdit.formula_terms.map((term) => ({
                biomarker: {
                  key: term.biomarker_id,
                  value:
                    findBiomarkerName(term.biomarker_id, [
                      allBiomarkers,
                      biomarkersList
                    ]) || String(term.biomarker_id)
                },
                coefficient: String(term.coefficient)
              }))
            : [emptyFormulaTerm()],
          low_high_good: biomarkerToEdit?.low_high_good
            ? lowHighGoodOptions.find(
              (option) => option.key === biomarkerToEdit.low_high_good
            ) || lowHighGoodOptions[0]
            : lowHighGoodOptions[0],
          blood_panels: biomarkerToEdit?.blood_panels?.length
            ? biomarkerToEdit?.blood_panels.map((panel: string) => ({
              key: panel,
              value: bloodPanelOptions.find(option => option.key === panel)?.value || panel
            }))
            : [],
          bodySystems: biomarkerToEdit?.body_systems
            ? biomarkerToEdit?.body_systems.map((bodySystem) => ({
              key: bodySystem.id,
              value: bodySystem.title
            }))
            : [],
          units: biomarkerToEdit?.measurement_unit
            ? {
              key: biomarkerToEdit?.measurement_unit,
              value: biomarkerMeasurementUnits.find(
                (unit) => unit.key === biomarkerToEdit?.measurement_unit
              )?.value
            }
            : { key: '', value: '' },
          foods_to_eat: biomarkerToEdit?.foods_to_eat?.length
            ? biomarkerToEdit?.foods_to_eat.map((food) => ({
              key: food.id,
              value: food.name
            }))
            : [],
          foods_to_avoid: biomarkerToEdit?.foods_to_avoid?.length
            ? biomarkerToEdit?.foods_to_avoid.map((food) => ({
              key: food.id,
              value: food.name
            }))
            : [],
          genderData:
            biomarkerToEdit &&
              (biomarkerToEdit.male_range_data ||
                biomarkerToEdit.female_range_data)
              ? [
                ...(biomarkerToEdit.male_range_data
                  ? [
                    {
                      gender: { key: 'male', value: 'Male' },
                      data_with_age: isAgeDependentRange(
                        biomarkerToEdit.male_range_data
                      )
                        ? Object.entries(
                          biomarkerToEdit.male_range_data
                        ).map(([rangeKey, values]: [string, any]) => {
                          const [age_from, age_to] = rangeKey
                            .split('-')
                            .map((v) => v.trim());
                          return {
                            age_from,
                            age_to,
                            low_from:
                              values.low_from === null
                                ? 'NA'
                                : values.low_from?.toString() || '',
                            low_to:
                              values.low_to === null
                                ? ((values as any).low_to_infinity || values.low_from !== null ? '∞' : 'NA')
                                : values.low_to?.toString() || '',
                            low_to_infinity: Boolean((values as any).low_to_infinity || (values.low_from !== null && values.low_to === null)),
                            below_optimal_from:
                              values.below_optimal_from === null
                                ? 'NA'
                                : values.below_optimal_from?.toString() ||
                                '',
                            below_optimal_to:
                              values.below_optimal_to === null
                                ? ((values as any).below_to_infinity || values.below_optimal_from !== null ? '∞' : 'NA')
                                : values.below_optimal_to?.toString() ||
                                '',
                            below_optimal_to_infinity: Boolean((values as any).below_to_infinity || (values.below_optimal_from !== null && values.below_optimal_to === null)),
                            above_optimal_from:
                              values.above_optimal_from === null
                                ? 'NA'
                                : values.above_optimal_from?.toString() ||
                                '',
                            above_optimal_to:
                              values.above_optimal_to === null
                                ? ((values as any).above_to_infinity || values.above_optimal_from !== null ? '∞' : 'NA')
                                : values.above_optimal_to?.toString() ||
                                '',
                            above_optimal_to_infinity: Boolean((values as any).above_to_infinity || (values.above_optimal_from !== null && values.above_optimal_to === null)),
                            optimal_from:
                              values.optimal_from === null
                                ? 'NA'
                                : values.optimal_from?.toString() || '',
                            optimal_to:
                              values.optimal_to === null
                                ? ((values as any).optimal_to_infinity || values.optimal_from !== null ? '∞' : 'NA')
                                : values.optimal_to?.toString() || '',
                            optimal_to_infinity: Boolean((values as any).optimal_to_infinity || (values.optimal_from !== null && values.optimal_to === null)),
                            high_from:
                              values.high_from === null
                                ? 'NA'
                                : values.high_from?.toString() || '',
                            high_to:
                              values.high_to === null
                                ? ((values as any).high_to_infinity || values.high_from !== null ? '∞' : 'NA')
                                : values.high_to?.toString() || '',
                            high_to_infinity: Boolean((values as any).high_to_infinity || values.high_to === '∞' || (values.high_from !== null && values.high_to === null))
                          };
                        })
                        : [
                          {
                            age_from: '',
                            age_to: '',
                            low_from:
                              (
                                biomarkerToEdit.male_range_data as GenderRangeData
                              )?.low_from === null
                                ? 'NA'
                                : (
                                  biomarkerToEdit.male_range_data as GenderRangeData
                                )?.low_from?.toString() || '',
                            low_to:
                              (
                                biomarkerToEdit.male_range_data as GenderRangeData
                              )?.low_to === null
                                ? ((biomarkerToEdit.male_range_data as any).low_to_infinity || (biomarkerToEdit.male_range_data as GenderRangeData)?.low_from !== null ? '∞' : 'NA')
                                : (
                                  biomarkerToEdit.male_range_data as GenderRangeData
                                )?.low_to?.toString() || '',
                            low_to_infinity: Boolean((biomarkerToEdit.male_range_data as any).low_to_infinity || ((biomarkerToEdit.male_range_data as GenderRangeData)?.low_from !== null && (biomarkerToEdit.male_range_data as GenderRangeData)?.low_to === null)),
                            below_optimal_from:
                              (
                                biomarkerToEdit.male_range_data as GenderRangeData
                              )?.below_optimal_from === null
                                ? 'NA'
                                : (
                                  biomarkerToEdit.male_range_data as GenderRangeData
                                )?.below_optimal_from?.toString() || '',
                            below_optimal_to:
                              (
                                biomarkerToEdit.male_range_data as GenderRangeData
                              )?.below_optimal_to === null
                                ? ((biomarkerToEdit.male_range_data as any).below_to_infinity || (biomarkerToEdit.male_range_data as GenderRangeData)?.below_optimal_from !== null ? '∞' : 'NA')
                                : (
                                  biomarkerToEdit.male_range_data as GenderRangeData
                                )?.below_optimal_to?.toString() || '',
                            below_optimal_to_infinity: Boolean((biomarkerToEdit.male_range_data as any).below_to_infinity || ((biomarkerToEdit.male_range_data as GenderRangeData)?.below_optimal_from !== null && (biomarkerToEdit.male_range_data as GenderRangeData)?.below_optimal_to === null)),
                            above_optimal_from:
                              (
                                biomarkerToEdit.male_range_data as GenderRangeData
                              )?.above_optimal_from === null
                                ? 'NA'
                                : (
                                  biomarkerToEdit.male_range_data as GenderRangeData
                                )?.above_optimal_from?.toString() || '',
                            above_optimal_to:
                              (
                                biomarkerToEdit.male_range_data as GenderRangeData
                              )?.above_optimal_to === null
                                ? ((biomarkerToEdit.male_range_data as any).above_to_infinity || (biomarkerToEdit.male_range_data as GenderRangeData)?.above_optimal_from !== null ? '∞' : 'NA')
                                : (
                                  biomarkerToEdit.male_range_data as GenderRangeData
                                )?.above_optimal_to?.toString() || '',
                            above_optimal_to_infinity: Boolean((biomarkerToEdit.male_range_data as any).above_to_infinity || ((biomarkerToEdit.male_range_data as GenderRangeData)?.above_optimal_from !== null && (biomarkerToEdit.male_range_data as GenderRangeData)?.above_optimal_to === null)),
                            optimal_from:
                              (biomarkerToEdit.male_range_data as GenderRangeData)?.optimal_from === null
                                ? 'NA'
                                : (biomarkerToEdit.male_range_data as GenderRangeData)?.optimal_from?.toString() || '',
                            optimal_to:
                              (biomarkerToEdit.male_range_data as GenderRangeData)?.optimal_to === null
                                ? ((biomarkerToEdit.male_range_data as any).optimal_to_infinity || (biomarkerToEdit.male_range_data as GenderRangeData)?.optimal_from !== null ? '∞' : 'NA')
                                : (biomarkerToEdit.male_range_data as GenderRangeData)?.optimal_to?.toString() || '',
                            optimal_to_infinity: Boolean((biomarkerToEdit.male_range_data as any).optimal_to_infinity || ((biomarkerToEdit.male_range_data as GenderRangeData)?.optimal_from !== null && (biomarkerToEdit.male_range_data as GenderRangeData)?.optimal_to === null)),
                            high_from:
                              (
                                biomarkerToEdit.male_range_data as GenderRangeData
                              )?.high_from === null
                                ? 'NA'
                                : (
                                  biomarkerToEdit.male_range_data as GenderRangeData
                                )?.high_from?.toString() || '',
                            high_to:
                              (
                                biomarkerToEdit.male_range_data as any
                              )?.high_to === null
                                ? ((biomarkerToEdit.male_range_data as any)?.high_to_infinity || (biomarkerToEdit.male_range_data as GenderRangeData)?.high_from !== null ? '∞' : 'NA')
                                : (
                                  biomarkerToEdit.male_range_data as any
                                )?.high_to?.toString() || '',
                            high_to_infinity: Boolean((biomarkerToEdit.male_range_data as any)?.high_to_infinity || (biomarkerToEdit.male_range_data as any)?.high_to === '∞' || ((biomarkerToEdit.male_range_data as GenderRangeData)?.high_from !== null && (biomarkerToEdit.male_range_data as any)?.high_to === null))
                          }
                        ],
                      low_from:
                        (biomarkerToEdit.male_range_data as GenderRangeData)
                          ?.low_from === null
                          ? 'NA'
                          : (
                            biomarkerToEdit.male_range_data as GenderRangeData
                          )?.low_from?.toString() || '',
                      low_to:
                        (biomarkerToEdit.male_range_data as GenderRangeData)
                          ?.low_to === null
                          ? ((biomarkerToEdit.male_range_data as any).low_to_infinity || (biomarkerToEdit.male_range_data as GenderRangeData)?.low_from !== null ? '∞' : 'NA')
                          : (
                            biomarkerToEdit.male_range_data as GenderRangeData
                          )?.low_to?.toString() || '',
                      low_to_infinity: Boolean((biomarkerToEdit.male_range_data as any).low_to_infinity || ((biomarkerToEdit.male_range_data as GenderRangeData)?.low_from !== null && (biomarkerToEdit.male_range_data as GenderRangeData)?.low_to === null)),

                      optimal_from:
                        (biomarkerToEdit.male_range_data as GenderRangeData)
                          ?.optimal_from === null
                          ? 'NA'
                          : (
                            biomarkerToEdit.male_range_data as GenderRangeData
                          )?.optimal_from?.toString() || '',
                      optimal_to:
                        (biomarkerToEdit.male_range_data as GenderRangeData)
                          ?.optimal_to === null
                          ? ((biomarkerToEdit.male_range_data as any).optimal_to_infinity || (biomarkerToEdit.male_range_data as GenderRangeData)?.optimal_from !== null ? '∞' : 'NA')
                          : (
                            biomarkerToEdit.male_range_data as GenderRangeData
                          )?.optimal_to?.toString() || '',
                      optimal_to_infinity: Boolean((biomarkerToEdit.male_range_data as any).optimal_to_infinity || ((biomarkerToEdit.male_range_data as GenderRangeData)?.optimal_from !== null && (biomarkerToEdit.male_range_data as GenderRangeData)?.optimal_to === null)),
                      high_from:
                        (biomarkerToEdit.male_range_data as GenderRangeData)
                          ?.high_from === null
                          ? 'NA'
                          : (
                            biomarkerToEdit.male_range_data as GenderRangeData
                          )?.high_from?.toString() || '',
                      high_to:
                        (biomarkerToEdit.male_range_data as any)
                          ?.high_to === null
                          ? ((biomarkerToEdit.male_range_data as any)?.high_to_infinity || (biomarkerToEdit.male_range_data as GenderRangeData)?.high_from !== null ? '∞' : 'NA')
                          : (
                            biomarkerToEdit.male_range_data as any
                          )?.high_to?.toString() || '',
                      high_to_infinity: Boolean((biomarkerToEdit.male_range_data as any)?.high_to_infinity || (biomarkerToEdit.male_range_data as any)?.high_to === '∞' || ((biomarkerToEdit.male_range_data as GenderRangeData)?.high_from !== null && (biomarkerToEdit.male_range_data as any)?.high_to === null)),
                      below_optimal_from:
                        (biomarkerToEdit.male_range_data as GenderRangeData)?.below_optimal_from === null
                          ? 'NA'
                          : (biomarkerToEdit.male_range_data as GenderRangeData)?.below_optimal_from?.toString() || '',

                      below_optimal_to:
                        (biomarkerToEdit.male_range_data as GenderRangeData)?.below_optimal_to === null
                          ? ((biomarkerToEdit.male_range_data as any).below_to_infinity || (biomarkerToEdit.male_range_data as GenderRangeData)?.below_optimal_from !== null ? '∞' : 'NA')
                          : (biomarkerToEdit.male_range_data as GenderRangeData)?.below_optimal_to?.toString() || '',
                      below_optimal_to_infinity: Boolean((biomarkerToEdit.male_range_data as any).below_to_infinity || ((biomarkerToEdit.male_range_data as GenderRangeData)?.below_optimal_from !== null && (biomarkerToEdit.male_range_data as GenderRangeData)?.below_optimal_to === null)),

                      above_optimal_from:
                        (biomarkerToEdit.male_range_data as GenderRangeData)?.above_optimal_from === null
                          ? 'NA'
                          : (biomarkerToEdit.male_range_data as GenderRangeData)?.above_optimal_from?.toString() || '',

                      above_optimal_to:
                        (biomarkerToEdit.male_range_data as GenderRangeData)?.above_optimal_to === null
                          ? ((biomarkerToEdit.male_range_data as any).above_to_infinity || (biomarkerToEdit.male_range_data as GenderRangeData)?.above_optimal_from !== null ? '∞' : 'NA')
                          : (biomarkerToEdit.male_range_data as GenderRangeData)?.above_optimal_to?.toString() || '',
                      above_optimal_to_infinity: Boolean((biomarkerToEdit.male_range_data as any).above_to_infinity || ((biomarkerToEdit.male_range_data as GenderRangeData)?.above_optimal_from !== null && (biomarkerToEdit.male_range_data as GenderRangeData)?.above_optimal_to === null)),

                    }
                  ]
                  : []),
                ...(biomarkerToEdit.female_range_data
                  ? [
                    {
                      gender: { key: 'female', value: 'Female' },
                      data_with_age: isAgeDependentRange(
                        biomarkerToEdit.female_range_data
                      )
                        ? Object.entries(
                          biomarkerToEdit.female_range_data
                        ).map(([rangeKey, values]: [string, any]) => {
                          const [age_from, age_to] = rangeKey
                            .split('-')
                            .map((v) => v.trim());
                          return {
                            age_from,
                            age_to,
                            low_from:
                              values.low_from === null
                                ? 'NA'
                                : values.low_from?.toString() || '',
                            low_to:
                              values.low_to === null
                                ? ((values as any).low_to_infinity || values.low_from !== null ? '∞' : 'NA')
                                : values.low_to?.toString() || '',
                            low_to_infinity: Boolean((values as any).low_to_infinity || (values.low_from !== null && values.low_to === null)),
                            below_optimal_from:
                              values.below_optimal_from === null
                                ? 'NA'
                                : values.below_optimal_from?.toString() ||
                                '',
                            below_optimal_to:
                              values.below_optimal_to === null
                                ? ((values as any).below_to_infinity || values.below_optimal_from !== null ? '∞' : 'NA')
                                : values.below_optimal_to?.toString() ||
                                '',
                            below_optimal_to_infinity: Boolean((values as any).below_to_infinity || (values.below_optimal_from !== null && values.below_optimal_to === null)),
                            above_optimal_from:
                              values.above_optimal_from === null
                                ? 'NA'
                                : values.above_optimal_from?.toString() ||
                                '',
                            above_optimal_to:
                              values.above_optimal_to === null
                                ? ((values as any).above_to_infinity || values.above_optimal_from !== null ? '∞' : 'NA')
                                : values.above_optimal_to?.toString() ||
                                '',
                            above_optimal_to_infinity: Boolean((values as any).above_to_infinity || (values.above_optimal_from !== null && values.above_optimal_to === null)),
                            optimal_from:
                              values.optimal_from === null
                                ? 'NA'
                                : values.optimal_from?.toString() || '',
                            optimal_to:
                              values.optimal_to === null
                                ? ((values as any).optimal_to_infinity || values.optimal_from !== null ? '∞' : 'NA')
                                : values.optimal_to?.toString() || '',
                            optimal_to_infinity: Boolean((values as any).optimal_to_infinity || (values.optimal_from !== null && values.optimal_to === null)),
                            high_from:
                              values.high_from === null
                                ? 'NA'
                                : values.high_from?.toString() || '',
                            high_to:
                              values.high_to === null
                                ? ((values as any).high_to_infinity || values.high_from !== null ? '∞' : 'NA')
                                : values.high_to?.toString() || '',
                            high_to_infinity: Boolean((values as any).high_to_infinity || values.high_to === '∞' || (values.high_from !== null && values.high_to === null))
                          };
                        })
                        : [
                          {
                            age_from: '',
                            age_to: '',
                            low_from:
                              (
                                biomarkerToEdit.female_range_data as GenderRangeData
                              )?.low_from === null
                                ? 'NA'
                                : (
                                  biomarkerToEdit.female_range_data as GenderRangeData
                                )?.low_from?.toString() || '',
                            low_to:
                              (
                                biomarkerToEdit.female_range_data as GenderRangeData
                              )?.low_to === null
                                ? ((biomarkerToEdit.female_range_data as any).low_to_infinity ? '∞' : 'NA')
                                : (
                                  biomarkerToEdit.female_range_data as GenderRangeData
                                )?.low_to?.toString() || '',
                            low_to_infinity: Boolean((biomarkerToEdit.female_range_data as any).low_to_infinity ?? false),

                            optimal_from:
                              (
                                biomarkerToEdit.female_range_data as GenderRangeData
                              )?.optimal_from === null
                                ? 'NA'
                                : (
                                  biomarkerToEdit.female_range_data as GenderRangeData
                                )?.optimal_from?.toString() || '',
                            optimal_to:
                              (
                                biomarkerToEdit.female_range_data as GenderRangeData
                              )?.optimal_to === null
                                ? ((biomarkerToEdit.female_range_data as any).optimal_to_infinity ? '∞' : 'NA')
                                : (
                                  biomarkerToEdit.female_range_data as GenderRangeData
                                )?.optimal_to?.toString() || '',
                            optimal_to_infinity: Boolean((biomarkerToEdit.female_range_data as any).optimal_to_infinity ?? false),
                            high_from:
                              (
                                biomarkerToEdit.female_range_data as GenderRangeData
                              )?.high_from === null
                                ? 'NA'
                                : (
                                  biomarkerToEdit.female_range_data as GenderRangeData
                                )?.high_from?.toString() || '',

                            high_to:
                              (
                                biomarkerToEdit.female_range_data as any
                              )?.high_to === null
                                ? ((biomarkerToEdit.female_range_data as any)?.high_to_infinity ? '∞' : 'NA')
                                : (
                                  biomarkerToEdit.female_range_data as any
                                )?.high_to?.toString() || '',
                            high_to_infinity: Boolean((biomarkerToEdit.female_range_data as any)?.high_to_infinity || (biomarkerToEdit.female_range_data as any)?.high_to === '∞'),
                            below_optimal_from:
                              (
                                biomarkerToEdit.female_range_data as GenderRangeData
                              )?.below_optimal_from === null
                                ? 'NA'
                                : (
                                  biomarkerToEdit.female_range_data as GenderRangeData
                                )?.below_optimal_from?.toString() || '',
                            below_optimal_to:
                              (
                                biomarkerToEdit.female_range_data as GenderRangeData
                              )?.below_optimal_to === null
                                ? ((biomarkerToEdit.female_range_data as any).below_to_infinity ? '∞' : 'NA')
                                : (
                                  biomarkerToEdit.female_range_data as GenderRangeData
                                )?.below_optimal_to?.toString() || '',
                            below_optimal_to_infinity: Boolean((biomarkerToEdit.female_range_data as any).below_to_infinity ?? false),
                            above_optimal_from:
                              (
                                biomarkerToEdit.female_range_data as GenderRangeData
                              )?.above_optimal_from === null
                                ? 'NA'
                                : (
                                  biomarkerToEdit.female_range_data as GenderRangeData
                                )?.above_optimal_from?.toString() || '',
                            above_optimal_to:
                              (
                                biomarkerToEdit.female_range_data as GenderRangeData
                              )?.above_optimal_to === null
                                ? ((biomarkerToEdit.female_range_data as any).above_to_infinity ? '∞' : 'NA')
                                : (
                                  biomarkerToEdit.female_range_data as GenderRangeData
                                )?.above_optimal_to?.toString() || '',
                            above_optimal_to_infinity: Boolean((biomarkerToEdit.female_range_data as any).above_to_infinity ?? false)
                          }
                        ],
                      low_from:
                        (
                          biomarkerToEdit.female_range_data as GenderRangeData
                        )?.low_from === null
                          ? 'NA'
                          : (
                            biomarkerToEdit.female_range_data as GenderRangeData
                          )?.low_from?.toString() || '',
                      low_to:
                        (
                          biomarkerToEdit.female_range_data as GenderRangeData
                        )?.low_to === null
                          ? ((biomarkerToEdit.female_range_data as any).low_to_infinity || (biomarkerToEdit.female_range_data as GenderRangeData)?.low_from !== null ? '∞' : 'NA')
                          : (
                            biomarkerToEdit.female_range_data as GenderRangeData
                          )?.low_to?.toString() || '',
                      low_to_infinity: Boolean((biomarkerToEdit.female_range_data as any).low_to_infinity || ((biomarkerToEdit.female_range_data as GenderRangeData)?.low_from !== null && (biomarkerToEdit.female_range_data as GenderRangeData)?.low_to === null)),
                      below_optimal_from:
                        (
                          biomarkerToEdit.female_range_data as GenderRangeData
                        )?.below_optimal_from === null
                          ? 'NA'
                          : (
                            biomarkerToEdit.female_range_data as GenderRangeData
                          )?.below_optimal_from?.toString() || '',
                      below_optimal_to:
                        (
                          biomarkerToEdit.female_range_data as GenderRangeData
                        )?.below_optimal_to === null
                          ? ((biomarkerToEdit.female_range_data as any).below_to_infinity || (biomarkerToEdit.female_range_data as GenderRangeData)?.below_optimal_from !== null ? '∞' : 'NA')
                          : (
                            biomarkerToEdit.female_range_data as GenderRangeData
                          )?.below_optimal_to?.toString() || '',
                      below_optimal_to_infinity: Boolean((biomarkerToEdit.female_range_data as any).below_to_infinity || ((biomarkerToEdit.female_range_data as GenderRangeData)?.below_optimal_from !== null && (biomarkerToEdit.female_range_data as GenderRangeData)?.below_optimal_to === null)),
                      above_optimal_from:
                        (
                          biomarkerToEdit.female_range_data as GenderRangeData
                        )?.above_optimal_from === null
                          ? 'NA'
                          : (
                            biomarkerToEdit.female_range_data as GenderRangeData
                          )?.above_optimal_from?.toString() || '',
                      above_optimal_to:
                        (
                          biomarkerToEdit.female_range_data as GenderRangeData
                        )?.above_optimal_to === null
                          ? ((biomarkerToEdit.female_range_data as any).above_to_infinity || (biomarkerToEdit.female_range_data as GenderRangeData)?.above_optimal_from !== null ? '∞' : 'NA')
                          : (
                            biomarkerToEdit.female_range_data as GenderRangeData
                          )?.above_optimal_to?.toString() || '',
                      above_optimal_to_infinity: Boolean((biomarkerToEdit.female_range_data as any).above_to_infinity || ((biomarkerToEdit.female_range_data as GenderRangeData)?.above_optimal_from !== null && (biomarkerToEdit.female_range_data as GenderRangeData)?.above_optimal_to === null)),
                      optimal_from:
                        (
                          biomarkerToEdit.female_range_data as GenderRangeData
                        )?.optimal_from === null
                          ? 'NA'
                          : (
                            biomarkerToEdit.female_range_data as GenderRangeData
                          )?.optimal_from?.toString() || '',
                      optimal_to:
                        (
                          biomarkerToEdit.female_range_data as GenderRangeData
                        )?.optimal_to === null
                          ? ((biomarkerToEdit.female_range_data as any).optimal_to_infinity || (biomarkerToEdit.female_range_data as GenderRangeData)?.optimal_from !== null ? '∞' : 'NA')
                          : (
                            biomarkerToEdit.female_range_data as GenderRangeData
                          )?.optimal_to?.toString() || '',
                      optimal_to_infinity: Boolean((biomarkerToEdit.female_range_data as any).optimal_to_infinity || ((biomarkerToEdit.female_range_data as GenderRangeData)?.optimal_from !== null && (biomarkerToEdit.female_range_data as GenderRangeData)?.optimal_to === null)),
                      high_from:
                        (
                          biomarkerToEdit.female_range_data as GenderRangeData
                        )?.high_from === null
                          ? 'NA'
                          : (
                            biomarkerToEdit.female_range_data as GenderRangeData
                          )?.high_from?.toString() || '',

                      high_to:
                        (
                          biomarkerToEdit.female_range_data as any
                        )?.high_to === null
                          ? ((biomarkerToEdit.female_range_data as any)?.high_to_infinity || (biomarkerToEdit.female_range_data as GenderRangeData)?.high_from !== null ? '∞' : 'NA')
                          : (
                            biomarkerToEdit.female_range_data as any
                          )?.high_to?.toString() || '',
                      high_to_infinity: Boolean((biomarkerToEdit.female_range_data as any)?.high_to_infinity || (biomarkerToEdit.female_range_data as any)?.high_to === '∞' || ((biomarkerToEdit.female_range_data as GenderRangeData)?.high_from !== null && (biomarkerToEdit.female_range_data as any)?.high_to === null))
                    }
                  ]
                  : [])
              ]
              : [
                {
                  gender: { key: '', value: '' },
                  data_with_age: [
                    {
                      age_from: '',
                      age_to: '',
                      low_from: '',
                      low_to: '',
                      low_to_infinity: false,
                      below_optimal_from: '',
                      below_optimal_to: '',
                      below_optimal_to_infinity: false,
                      above_optimal_from: '',
                      above_optimal_to: '',
                      above_optimal_to_infinity: false,
                      optimal_from: '',
                      optimal_to: '',
                      optimal_to_infinity: false,
                      high_from: '',
                      high_to: '',
                      high_to_infinity: false
                    }
                  ],
                  low_from: '',
                  low_to: '',
                  low_to_infinity: false,
                  below_optimal_from: '',
                  below_optimal_to: '',
                  below_optimal_to_infinity: false,
                  above_optimal_from: '',
                  above_optimal_to: '',
                  above_optimal_to_infinity: false,
                  optimal_from: '',
                  optimal_to: '',
                  optimal_to_infinity: false,
                  high_from: '',
                  high_to: '',
                  high_to_infinity: false
                }
              ]
        }}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <>
          <AffineFormulaTermsLabelSync
            allBiomarkers={allBiomarkers}
            formulaTerms={values.formula_terms}
            formulaTypeKey={values.formula_type.key}
            setFieldValue={setFieldValue}
          />

          <Form className='update-biomarker-form'>
            <div className='biomarkers-form-header'>Main Info</div>
            <div className='biomarkers-form-body'>
              <TextInput
                title='Name *'
                name='name'
                placeholder='Enter name'
                type='text'
                onChange={(e) => setFieldValue('name', e.target.value)}
                value={values.name}
              />
              <div>
                <label className='checkbox-container mt-0 d-flex align-items-center gap-10'>
                  <input
                    type='checkbox'
                    checked={values.is_derived}
                    onChange={(e) => {
                      setFieldValue('is_derived', e.target.checked);
                      if (e.target.checked) {
                        setFieldValue(
                          'biomarker_type',
                          biomarkerTypesList.find(
                            (t) => t.key === BiomarkerType.RANGES
                          )
                        );
                        setFieldValue('formula_type', formulaTypeOptions[0]);
                      } else {
                        setFieldValue('formula_type', formulaTypeOptions[0]);
                        setFieldValue('numerator', { key: '', value: '' });
                        setFieldValue('denominator', { key: '', value: '' });
                        setFieldValue('scale_target', scaleTargetOptions[0]);
                        setFieldValue('scale_factor', '');
                        setFieldValue('formula_constant', '');
                        setFieldValue('formula_terms', [emptyFormulaTerm()]);
                      }
                    }}
                  />
                  <span className='font-16'>Derived?</span>
                </label>
              </div>
              {values.is_derived && (
                <div className='derived-calculation-section'>
                  <div className='section-title'>Calculation</div>
                  <Select
                    title='Formula type *'
                    name='formula_type'
                    placeholder='Select formula type'
                    options={formulaTypeOptions}
                    selectedOption={values.formula_type}
                    onChange={(option) => {
                      setFieldValue('formula_type', option);
                      if (option.key === DerivedBiomarkerFormulaType.AFFINE) {
                        setFieldValue('numerator', { key: '', value: '' });
                        setFieldValue('denominator', { key: '', value: '' });
                        setFieldValue('scale_target', scaleTargetOptions[0]);
                        setFieldValue('scale_factor', '');
                        if (!values.formula_terms.length) {
                          setFieldValue('formula_terms', [emptyFormulaTerm()]);
                        }
                      } else {
                        setFieldValue('formula_constant', '');
                        setFieldValue('formula_terms', [emptyFormulaTerm()]);
                      }
                    }}
                    isError={false}
                  />

                  {values.formula_type.key === DerivedBiomarkerFormulaType.RATIO && (
                    <>
                      <div className='d-flex gap-10 top-space'>
                        <div className='w-100'>
                          <div className='section-title'>Numerator *</div>
                          <Select
                            name='numerator'
                            placeholder='Select biomarker'
                            options={biomarkerOptions}
                            selectedOption={values.numerator}
                            onChange={(option) => setFieldValue('numerator', option)}
                            isError={false}
                          />
                        </div>
                        <div className='divider-top-space'>/</div>
                        <div className='w-100'>
                          <div className='section-title'>Denominator *</div>
                          <Select
                            name='denominator'
                            placeholder='Select biomarker'
                            options={biomarkerOptions}
                            selectedOption={values.denominator}
                            onChange={(option) => setFieldValue('denominator', option)}
                            isError={false}
                          />
                        </div>
                      </div>
                      <div className='d-flex gap-10 top-space'>
                        <div className='w-100'>
                          <div className='section-title'>Scale target (optional)</div>
                          <Select
                            name='scale_target'
                            placeholder='None'
                            options={scaleTargetOptions}
                            selectedOption={values.scale_target}
                            onChange={(option) => {
                              setFieldValue('scale_target', option);
                              if (!option.key) {
                                setFieldValue('scale_factor', '');
                              }
                            }}
                            isError={false}
                          />
                        </div>
                        <div className='w-100'>
                          <TextInput
                            title='Scale factor (optional)'
                            name='scale_factor'
                            placeholder='e.g. 100'
                            type='number'
                            onChange={(e) =>
                              setFieldValue('scale_factor', e.target.value)
                            }
                            value={values.scale_factor}
                            isDisabled={!values.scale_target.key}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {values.formula_type.key === DerivedBiomarkerFormulaType.AFFINE && (
                    <div className='affine-formula-section top-space'>
                      <div className='affine-formula-hint'>
                        Result = Σ (coefficient × biomarker) + constant.
                        Example: Corrected Calcium = 1.0×Calcium + (−0.8)×Albumin + 3.2
                      </div>
                      <TextInput
                        title='Constant (optional)'
                        name='formula_constant'
                        placeholder='e.g. 3.2'
                        type='number'
                        onChange={(e) =>
                          setFieldValue('formula_constant', e.target.value)
                        }
                        value={values.formula_constant}
                      />
                      <div className='affine-terms-header'>
                        <div className='section-title'>Terms *</div>
                        <button
                          type='button'
                          className='affine-add-term-btn'
                          onClick={() =>
                            setFieldValue('formula_terms', [
                              ...values.formula_terms,
                              emptyFormulaTerm()
                            ])
                          }
                        >
                          + Add term
                        </button>
                      </div>
                      {values.formula_terms.map((term, index) => (
                        <div className='affine-term-row' key={`formula-term-${index}`}>
                          <div className='w-100'>
                            <div className='section-title'>Biomarker *</div>
                            <Select
                              name={`formula_terms.${index}.biomarker`}
                              placeholder='Select biomarker'
                              options={biomarkerOptions}
                              selectedOption={term.biomarker}
                              onChange={(option) => {
                                const nextTerms = [...values.formula_terms];
                                nextTerms[index] = {
                                  ...nextTerms[index],
                                  biomarker: option
                                };
                                setFieldValue('formula_terms', nextTerms);
                              }}
                              isError={false}
                            />
                          </div>
                          <div className='affine-term-coefficient'>
                            <TextInput
                              title='Coefficient *'
                              name={`formula_terms.${index}.coefficient`}
                              placeholder='e.g. -0.8'
                              type='number'
                              onChange={(e) => {
                                const nextTerms = [...values.formula_terms];
                                nextTerms[index] = {
                                  ...nextTerms[index],
                                  coefficient: e.target.value
                                };
                                setFieldValue('formula_terms', nextTerms);
                              }}
                              value={term.coefficient}
                            />
                          </div>
                          <button
                            type='button'
                            className='affine-remove-term-btn'
                            disabled={values.formula_terms.length <= 1}
                            onClick={() => {
                              if (values.formula_terms.length <= 1) return;
                              setFieldValue(
                                'formula_terms',
                                values.formula_terms.filter((_, i) => i !== index)
                              );
                            }}
                            aria-label='Remove term'
                          >
                            <BinIcon />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <MultiSelect
                title='Choose body system(s) *'
                isError={!!(touched.bodySystems && errors.bodySystems)}
                name='body_system'
                selectedOptions={values.bodySystems}
                onChange={(option) =>
                  setFieldValue(
                    'bodySystems',
                    values.bodySystems.some((item) => item.key === option.key)
                      ? values.bodySystems.filter(
                        (item) => item.key !== option.key
                      )
                      : [...values.bodySystems, option]
                  )
                }
                placeholder='Select the body system'
                options={bodySystemsOptions}
              />
              <Select
                title='Type of biomarker *'
                placeholder='Choose type of biomarker'
                isError={!!(touched.units && errors.units)}
                name='type_of_biomarker'
                selectedOption={values.biomarker_type}
                onChange={(option) => setFieldValue('biomarker_type', option)}
                options={biomarkerTypesList}
                isDisable={values.is_derived}
              />

              <MultiSelect
                title='Blood Panels (optional)'
                isError={!!(touched.blood_panels && errors.blood_panels)}
                name='blood_panels'
                selectedOptions={values.blood_panels}
                onChange={(option) =>
                  setFieldValue(
                    'blood_panels',
                    values.blood_panels.some((item) => item.key === option.key)
                      ? values.blood_panels.filter(
                        (item) => item.key !== option.key
                      )
                      : [...values.blood_panels, option]
                  )
                }
                placeholder='Select blood panels'
                options={bloodPanelOptions}
              />

              {(['UNITS', 'RANGES'] as BiomarkerType[]).includes(
                values.biomarker_type.key
              ) && (
                  <Select
                    title='Units of measurement *'
                    placeholder='mg/dL'
                    isError={!!(touched.units && errors.units)}
                    name='units'
                    selectedOption={values.units}
                    onChange={(option) => setFieldValue('units', option)}
                    options={biomarkerMeasurementUnits}
                  />
                )}
              {values.biomarker_type.key === 'BINARY' && (
                <div className='main-info-container'>
                  <div className='section-title'>Value Meaning</div>
                  <div className='inputs-wrapper'>
                    <div className='radio-group d-flex flex-column gap-10'>
                      <div>
                        <label className='d-flex align-items-center gap-10'>
                          <input
                            type='radio'
                            name='value_meaning'
                            checked={
                              values.value_meaning === 'POSITIVE_ABNORMAL'
                            }
                            value='POSITIVE_ABNORMAL'
                            onChange={(e) => {
                              setFieldValue('value_meaning', e.target.value);
                            }}
                          />
                          Positive = abnormal / detected
                        </label>
                      </div>
                      <div>
                        <label className='d-flex align-items-center gap-10'>
                          <input
                            type='radio'
                            name='value_meaning'
                            checked={values.value_meaning === 'POSITIVE_NORMAL'}
                            value='POSITIVE_NORMAL'
                            onChange={(e) => {
                              setFieldValue('value_meaning', e.target.value);
                            }}
                          />
                          Positive = normal / desirable
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {values.biomarker_type.key === 'RANGES' && (
                <>
                  <div>
                    <label className='checkbox-container mt-0 d-flex align-items-center gap-10'>
                      <input
                        type='checkbox'
                        checked={values.age_dependent}
                        onChange={(e) =>
                          setFieldValue(`age_dependent`, e.target.checked)
                        }
                      />
                      <span className='font-16'>Age dependent</span>
                    </label>
                  </div>
                  {values.genderData.map((_, index) => (
                    <GenderValuesBlock key={index} dataIndex={index} />
                  ))}
                </>
              )}
              <Select
                title='Low/High Good (optional)'
                placeholder='Normal'
                isError={false}
                name='low_high_good'
                selectedOption={values.low_high_good}
                onChange={(option) => setFieldValue('low_high_good', option)}
                options={lowHighGoodOptions}
              />
              <TextInput
                title='Lab-BM-ID'
                name='lab_bm_id'
                placeholder='Enter Lab-BM-ID'
                onChange={(e) => setFieldValue('lab_bm_id', e.target.value)}
                value={values.lab_bm_id}
              />
              <TextInput
                title='Catalog ID'
                name='catalog_id'
                placeholder='CAT-12345'
                onChange={(e) => setFieldValue('catalog_id', e.target.value)}
                value={values.catalog_id}
              />
              <TextInput
                title='Price'
                name='price'
                placeholder='Enter price'
                type='number'
                onChange={(e) => setFieldValue('price', e.target.value)}
                value={values.price}
              />
              <TextArea
                title='Description'
                name='description'
                placeholder='Enter description'
                onChange={(e) => setFieldValue('description', e.target.value)}
                value={values.description}
              />
              <MultiSelectSearch
                title='Foods to eat'
                isError={!!(touched.foods_to_eat && errors.foods_to_eat)}
                name='food_to_eat'
                selectedOptions={values.foods_to_eat}
                onChange={(option) =>
                  setFieldValue(
                    'foods_to_eat',
                    values.foods_to_eat.some((item) => item.key === option.key)
                      ? values.foods_to_eat.filter(
                        (item) => item.key !== option.key
                      )
                      : [...values.foods_to_eat, option]
                  )
                }
                placeholder='Select the foods to eat'
                options={foodListOptions}
              />
              <MultiSelectSearch
                title='Foods to avoid'
                isError={!!(touched.foods_to_avoid && errors.foods_to_avoid)}
                name='food_to_avoid'
                selectedOptions={values.foods_to_avoid}
                onChange={(option) =>
                  setFieldValue(
                    'foods_to_avoid',
                    values.foods_to_avoid.some(
                      (item) => item.key === option.key
                    )
                      ? values.foods_to_avoid.filter(
                        (item) => item.key !== option.key
                      )
                      : [...values.foods_to_avoid, option]
                  )
                }
                placeholder='Select the foods to avoid'
                options={foodListOptions}
              />
              <TextArea
                title='Lifestyle Changes'
                name='lifestyle_changes'
                placeholder='Enter lifestyle changes'
                onChange={(e) =>
                  setFieldValue('lifestyle_changes', e.target.value)
                }
                value={values.lifestyle_changes}
              />
              <TextArea
                title='Medical Follow-up'
                name='follow_up'
                placeholder='Enter medical follow-up'
                onChange={(e) => setFieldValue('follow_up', e.target.value)}
                value={values.follow_up}
              />
              {/* <TextArea
                title='Medication contraindications'
                name='contraindications'
                placeholder='Enter medication contraindications'
                onChange={(e) =>
                  setFieldValue('contraindications', e.target.value)
                }
                value={values.contraindications}
              /> */}
              <Select
                title='Retest time period'
                placeholder='Select retest time period'
                isError={false}
                name='retest_time_period'
                selectedOption={values.retest_time_period}
                onChange={(option) =>
                  setFieldValue('retest_time_period', option)
                }
                options={retestTimePeriodOptions}
              />
            </div>
            <div className='modal-actions'>
              <Button
                text={biomarkerToEdit ? 'Save biomarker' : 'Add biomarker'}
                type='submit'
                isDisabled={
                  !values.name ||
                  !values.bodySystems.length ||
                  (['UNITS', 'RANGES'].includes(values.biomarker_type.key) &&
                    !values.units.key) ||
                  (values.is_derived &&
                    values.formula_type.key === DerivedBiomarkerFormulaType.RATIO &&
                    (!values.numerator.key ||
                      !values.denominator.key ||
                      (values.scale_target.key && values.scale_factor === '') ||
                      (!values.scale_target.key && values.scale_factor !== ''))) ||
                  (values.is_derived &&
                    values.formula_type.key === DerivedBiomarkerFormulaType.AFFINE &&
                    (values.formula_terms.length === 0 ||
                      values.formula_terms.some(
                        (term) =>
                          !term.biomarker.key ||
                          term.coefficient === '' ||
                          Number.isNaN(Number(term.coefficient))
                      ) ||
                      (values.formula_constant !== '' &&
                        Number.isNaN(Number(values.formula_constant))))) ||
                  (values.biomarker_type.key === 'RANGES' &&
                    (values.age_dependent
                      ? !values.genderData.every(
                        (gender) =>
                          Array.isArray(gender.data_with_age) &&
                          gender.data_with_age.every(
                            (ageData) =>
                              isValidField(ageData.age_from) &&
                              isValidField(ageData.age_to) &&
                              isValidField(ageData.below_optimal_from) &&
                              isValidField(ageData.below_optimal_to) &&
                              isValidField(ageData.low_to) &&
                              isValidField(ageData.above_optimal_from) &&
                              isValidField(ageData.above_optimal_to) &&
                              isValidField(ageData.optimal_from) &&
                              isValidField(ageData.optimal_to) &&
                              isValidField(ageData.high_from) &&
                              isValidField(ageData.high_to)
                          )
                      )
                      : !values.genderData.every(
                        (gender) =>
                          gender.gender?.key &&
                          isValidField(gender.below_optimal_from) &&
                          isValidField(gender.below_optimal_to) &&
                          isValidField(gender.low_to) &&
                          isValidField(gender.above_optimal_from) &&
                          isValidField(gender.above_optimal_to) &&
                          isValidField(gender.optimal_from) &&
                          isValidField(gender.optimal_to) &&
                          isValidField(gender.high_from) &&
                          isValidField(gender.high_to)
                      )))
                }
              />
              {biomarkerToEdit && (
                <div
                  className='delete-btn-biomarker'
                  onClick={handleDeleteClick}
                >
                  <BinIcon className='bin-icon-biomarker' />
                  <span>Delete biomarker</span>
                </div>
              )}
              {error && <div className='error-message'>{error}</div>}
            </div>
          </Form>
          </>
        )}
      </Formik>
      {isDeleteBiomarkerModalOpened && (
        <ConfirmModal
          isOpen={isDeleteBiomarkerModalOpened}
          onClose={handleCloseDeleteModalClick}
          onConfirmClick={handleDelete}
          title='Delete this FAQ?'
          subtitle='This action will permanently remove the FAQ. Once deleted, it cannot be restored.'
          loading={isLoading}
        />
      )}
    </Modal>
  );
};
